import { useState } from 'react'
import {
  apiErrorMessage,
  useAdminFraudSignal,
  useAdminFraudSignals,
  useResolveFraudSignal,
  type AdminFraudSignal,
  type FraudListParams,
} from '../lib/api'
import { useAdminAuth } from '../lib/auth'
import { fmtDateTime } from '../lib/format'

const SEV_TONE: Record<string, string> = {
  critical: 'admin-pill admin-pill--danger',
  high:     'admin-pill admin-pill--danger',
  medium:   'admin-pill admin-pill--warning',
  low:      'admin-pill admin-pill--info',
}

function SeverityPill({ severity }: { severity: 'low' | 'medium' | 'high' | 'critical' }) {
  return <span className={SEV_TONE[severity] ?? 'admin-pill'}>{severity}</span>
}

export function FraudPanelPage() {
  const [params, setParams] = useState<FraudListParams>({ unresolved: true, page: 1, perPage: 50 })
  const [selected, setSelected] = useState<string | null>(null)
  const list = useAdminFraudSignals(params)

  return (
    <>
      <header className="page-header">
        <div className="page-header__title-row">
          <h1 className="page-header__title">Fraud signals</h1>
          <span className="page-header__subtitle">Triage suspicious activity detected by the risk engine.</span>
        </div>
      </header>

      <div className="filter-bar" style={{ marginBottom: 'var(--space-4)' }}>
        <div className="filter-bar__group">
          <label className="field__label" htmlFor="fraud-sev">Severity</label>
          <select id="fraud-sev" className="field__input" style={{ width: 160 }}
            value={params.severity ?? ''}
            onChange={(e) => setParams((p) => ({ ...p, severity: (e.target.value || undefined) as FraudListParams['severity'], page: 1 }))}>
            <option value="">All</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>
        </div>
        <div className="filter-bar__group">
          <label className="field__label" htmlFor="fraud-open">View</label>
          <select id="fraud-open" className="field__input" style={{ width: 160 }}
            value={params.unresolved ? '1' : '0'}
            onChange={(e) => setParams((p) => ({ ...p, unresolved: e.target.value === '1', page: 1 }))}>
            <option value="1">Unresolved only</option>
            <option value="0">All</option>
          </select>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-5)' }}>
        <div className="admin-table">
          <table>
            <thead>
              <tr>
                <th>Type</th>
                <th>Severity</th>
                <th>Resolved</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {list.isLoading && (
                <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--text-tertiary)' }}>Loading…</td></tr>
              )}
              {list.data && list.data.signals.length === 0 && (
                <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--text-tertiary)' }}>No signals.</td></tr>
              )}
              {list.data && list.data.signals.map((s) => (
                <tr key={s.id} onClick={() => setSelected(s.id)} style={{ cursor: 'pointer', background: selected === s.id ? 'var(--bg-subtle)' : undefined }}>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>{s.signalType}</td>
                  <td><SeverityPill severity={s.severity} /></td>
                  <td>{s.resolved ? '✓' : '—'}</td>
                  <td style={{ color: 'var(--text-secondary)' }}>{fmtDateTime(s.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <FraudDetail id={selected} />
      </div>
    </>
  )
}

function FraudDetail({ id }: { id: string | null }) {
  const q = useAdminFraudSignal(id ?? undefined)
  const resolve = useResolveFraudSignal()
  const { user } = useAdminAuth()
  const canResolve = user?.role === 'admin' || user?.role === 'super_admin'
  const [notes, setNotes] = useState('')

  if (!id) {
    return (
      <div className="empty-state">
        <p style={{ color: 'var(--text-tertiary)', textAlign: 'center', padding: 'var(--space-6)' }}>
          Select a signal to view details.
        </p>
      </div>
    )
  }
  if (q.isLoading) return <div style={{ padding: 'var(--space-4)', color: 'var(--text-secondary)' }}>Loading…</div>
  if (q.isError || !q.data) {
    return <div className="auth-card__error" style={{ display: 'flex' }}>{apiErrorMessage(q.error)}</div>
  }
  const s: AdminFraudSignal = q.data.signal

  return (
    <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: 'var(--space-5)' }}>
      <h3 style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', margin: '0 0 var(--space-3)', fontSize: 'var(--text-md)', fontWeight: 'var(--weight-semibold)' }}>
        {s.signalType} <SeverityPill severity={s.severity} />
      </h3>
      <dl style={{ display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: 'var(--space-2)', columnGap: 'var(--space-3)', fontSize: 'var(--text-sm)', margin: '0 0 var(--space-3)' }}>
        <dt style={{ color: 'var(--text-secondary)' }}>User</dt>
        <dd style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>{s.userId ?? '—'}</dd>
        <dt style={{ color: 'var(--text-secondary)' }}>Created</dt>
        <dd>{fmtDateTime(s.createdAt)}</dd>
        <dt style={{ color: 'var(--text-secondary)' }}>Resolved</dt>
        <dd>{s.resolved ? `${s.resolution} (${fmtDateTime(s.resolvedAt)})` : '—'}</dd>
      </dl>
      <pre style={{ overflowX: 'auto', background: 'var(--bg-subtle)', borderRadius: 'var(--radius-sm)', padding: 'var(--space-3)', fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>
        {JSON.stringify(s.details, null, 2)}
      </pre>

      {!s.resolved && canResolve && (
        <div style={{ marginTop: 'var(--space-4)' }}>
          <label className="field__label" htmlFor="fraud-notes">Notes</label>
          <textarea id="fraud-notes" className="field__input" style={{ minHeight: 80, marginTop: 'var(--space-1)' }}
            value={notes} onChange={(e) => setNotes(e.target.value)} />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
            <button type="button" className="btn btn--danger btn--sm" disabled={resolve.isPending}
              onClick={() => resolve.mutate({ id: s.id, resolution: 'confirmed_fraud', notes: notes || undefined })}>
              Confirmed fraud
            </button>
            <button type="button" className="btn btn--secondary btn--sm" disabled={resolve.isPending}
              onClick={() => resolve.mutate({ id: s.id, resolution: 'false_positive', notes: notes || undefined })}>
              False positive
            </button>
            <button type="button" className="btn btn--secondary btn--sm" disabled={resolve.isPending}
              onClick={() => resolve.mutate({ id: s.id, resolution: 'requires_more_info', notes: notes || undefined })}>
              Requires more info
            </button>
          </div>
          {resolve.isError && (
            <div className="auth-card__error" style={{ display: 'flex', marginTop: 'var(--space-2)' }}>{apiErrorMessage(resolve.error)}</div>
          )}
        </div>
      )}
    </div>
  )
}
